/* Primitive tokens: raw values, named by what they are. Components never read these
   directly; they read the semantic layer. Values and contrast checks: PRISM_SPEC.md section 1. */

export const color = {
  void: '#000000',
  obsidian1: '#08080A',
  obsidian2: '#0B0B0E',
  obsidian3: '#111114',
  hair1: 'rgba(255,255,255,0.06)',
  hair2: 'rgba(255,255,255,0.10)',
  hair3: 'rgba(255,255,255,0.16)',
  ink1: '#F2F2F0',
  ink2: '#A3A3A8',
  ink3: '#8E8E94',
  ink4: '#6C6C72', // non-text only: fails AA as text on void
  up: '#3DC48E',
  down: '#E4625B',
} as const;

/** The spectrum: identity light, never a data encoding (spec 1.2). */
export const band = {
  core: '#FFFFFF',
  radar: '#79AEFF',
  lens: '#8FE6FF',
  pulse: '#A58CFF',
  impact: '#FFB877',
  confluence: '#F4A3CB',
  memory: '#E6D3AA',
} as const;

export const space = { s2: 2, s4: 4, s8: 8, s12: 12, s16: 16, s24: 24, s32: 32, s48: 48, s64: 64, s96: 96 } as const;

export const radius = { obsidian: 4, glass: 10, pill: 999, input: 6 } as const;

export const font = {
  display: "'Panchang', 'Switzer', system-ui, sans-serif",
  text: "'Switzer', system-ui, -apple-system, 'Segoe UI', sans-serif",
  /** the Core desk only: figures, codes, axes and tables */
  mono: "'IBM Plex Mono', ui-monospace, 'SF Mono', Menlo, monospace",
} as const;

/** size / line-height in px, weight, tracking in em */
export const type = {
  wordmark: { family: 'display', size: 20, line: 1, weight: 300, tracking: 0.18 },
  display: { family: 'display', size: 32, line: 1.1, weight: 300, tracking: 0.04 },
  heroFigure: { family: 'text', size: 64, line: 1, weight: 300, tracking: -0.02 },
  figureL: { family: 'text', size: 34, line: 1.05, weight: 400, tracking: -0.015 },
  figureM: { family: 'text', size: 22, line: 1.1, weight: 500, tracking: -0.01 },
  title: { family: 'text', size: 16, line: 1.3, weight: 500, tracking: 0 },
  body: { family: 'text', size: 14, line: 1.5, weight: 400, tracking: 0 },
  label: { family: 'text', size: 12, line: 1.35, weight: 400, tracking: 0 },
  axis: { family: 'text', size: 11, line: 1.2, weight: 400, tracking: 0 },
} as const;

export const motion = {
  cameraMs: { min: 650, max: 900 },
  expoOut: 'cubic-bezier(0.16, 1, 0.3, 1)',
  easeOut: 'cubic-bezier(0.23, 1, 0.32, 1)',
  spring: { stiffness: 170, damping: 26 },
  subviewMs: 240,
  uiMs: 160,
  valueMs: 400,
  reducedMs: 150,
} as const;

/** Light levels L1 to L4 (spec 1.6). Aura sizes in px, opacities 0..1. */
export const light = {
  L1: { line: 1, lineAlpha: 0.35, aura: 0, auraAlpha: 0 },
  L2: { line: 1, lineAlpha: 1, aura: 6, auraAlpha: 0.22 },
  L3: { line: 1.5, lineAlpha: 1, aura: 16, auraAlpha: 0.4 },
  L4: { line: 1.5, lineAlpha: 1, aura: 16, auraAlpha: 0.4, breatheMs: 2400, breathe: 0.15 },
  budget: { atL2: 5, atL3: 2 },
  proximity: 0.0015,
} as const;
